import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';

/**
 * R5: board annotations, like Lichess and Chess.com. Drawings stay until Clear,
 * a move, or the same gesture again; taps and clicks never clear them.
 *
 * - Mouse: right-click a square to highlight it, right-drag to draw an arrow.
 * - Touch (optional, Settings): press and hold a square for HOLD_MS (a light tap
 *   confirms), then drag for an arrow or let go for a highlight.
 * - Pen tool: while it's on, a tap highlights and a drag draws an arrow, with
 *   any finger, pen or mouse button; pieces don't move.
 */
export const HOLD_MS = 350;
/** A finger that moves further than this before the hold is a normal drag. */
const SLOP_PX = 8;

export interface Arrow {
  startSquare: string;
  endSquare: string;
  color: string;
}
/** The board library's own arrow colour. */
export const ARROW_COLOR = '#ffaa00';
/** Chess.com's right-click highlight. */
export const MARK_COLOR = '#eb6150cc';

/** Highlights and arrows belong to one position; a new position starts clean. */
export interface Marks {
  fen: string;
  squares: string[];
  arrows: Arrow[];
}
export const toggleSquare = (marks: Marks, square: string): Marks => ({
  ...marks,
  squares: marks.squares.includes(square)
    ? marks.squares.filter((s) => s !== square)
    : [...marks.squares, square],
});
export const toggleArrow = (marks: Marks, from: string, to: string): Marks => ({
  ...marks,
  arrows: marks.arrows.some((a) => a.startSquare === from && a.endSquare === to)
    ? marks.arrows.filter((a) => !(a.startSquare === from && a.endSquare === to))
    : [...marks.arrows, { startSquare: from, endSquare: to, color: ARROW_COLOR }],
});

/** One arrow per start and end square (the first wins: the hint, then drawings). */
export const uniqueArrows = (arrows: Arrow[]) =>
  arrows.filter(
    (a, i) =>
      arrows.findIndex((b) => b.startSquare === a.startSquare && b.endSquare === a.endSquare) === i,
  );

export const squareAt = (x: number, y: number) =>
  (document.elementFromPoint(x, y)?.closest('[data-board-square]') as HTMLElement | null)?.dataset
    .boardSquare;

interface Gesture {
  pointerId: number;
  x: number;
  y: number;
  from: string;
  over: string;
  /** Drawing has begun (at once for the mouse's right button and Pen; after the hold on touch). */
  active: boolean;
  /** Once drawing, the board library must not turn the moves into a piece drag. */
  block: boolean;
  timer?: number;
  cleanup: () => void;
}

export function useBoardDrawing({
  pen,
  holdToDraw,
  onHold,
  onDraw,
}: {
  pen: boolean;
  holdToDraw: boolean;
  onHold: () => void;
  onDraw: (from: string, to: string) => void;
}) {
  const gesture = useRef<Gesture | null>(null);
  // The press that drew something must not also count as a tap on its square.
  const swallowTap = useRef(false);
  const [preview, setPreview] = useState<{ from: string; to: string }>();
  const handlers = useRef({ onHold, onDraw });
  useEffect(() => {
    handlers.current = { onHold, onDraw };
  });
  const cancel = useCallback(() => {
    const g = gesture.current;
    if (!g) return;
    window.clearTimeout(g.timer);
    g.cleanup();
    gesture.current = null;
    setPreview(undefined);
  }, []);
  useEffect(() => cancel, [cancel]);
  useEffect(() => {
    const hidden = () => {
      if (document.hidden) cancel();
    };
    window.addEventListener('blur', cancel);
    document.addEventListener('visibilitychange', hidden);
    return () => {
      window.removeEventListener('blur', cancel);
      document.removeEventListener('visibilitychange', hidden);
    };
  }, [cancel]);

  const begin = (e: PointerEvent<HTMLElement>, from: string, active: boolean) => {
    const move = (ev: globalThis.PointerEvent) => {
      const g = gesture.current;
      if (!g || ev.pointerId !== g.pointerId) return;
      if (!g.active) {
        if (Math.hypot(ev.clientX - g.x, ev.clientY - g.y) > SLOP_PX) cancel();
        return;
      }
      if (g.block) {
        // Keep the board library from turning this into a piece drag.
        ev.stopImmediatePropagation();
        ev.preventDefault();
      }
      const over = squareAt(ev.clientX, ev.clientY);
      if (over && over !== g.over) {
        g.over = over;
        setPreview({ from: g.from, to: over });
      }
    };
    const end = (ev: globalThis.PointerEvent) => {
      const g = gesture.current;
      if (!g || ev.pointerId !== g.pointerId) return;
      if (g.active) {
        // iOS can cancel a held touch (system gestures): keep what was drawn so far.
        const to = ev.type === 'pointerup' ? squareAt(ev.clientX, ev.clientY) : g.over;
        // A mouse drag released off the board draws nothing.
        if (to) handlers.current.onDraw(g.from, to);
      }
      cancel();
    };
    window.addEventListener('pointermove', move, { capture: true, passive: false });
    window.addEventListener('pointerup', end, true);
    window.addEventListener('pointercancel', end, true);
    gesture.current = {
      pointerId: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      from,
      over: from,
      active,
      block: e.pointerType !== 'mouse' || e.button === 0,
      cleanup: () => {
        window.removeEventListener('pointermove', move, true);
        window.removeEventListener('pointerup', end, true);
        window.removeEventListener('pointercancel', end, true);
      },
    };
    if (active) {
      swallowTap.current = true;
      setPreview({ from, to: from });
    }
  };

  /** Runs in the capture phase, before the board library sees the press. */
  const onPointerDownCapture = (e: PointerEvent<HTMLElement>) => {
    // A new press is a new gesture: its tap counts again.
    swallowTap.current = false;
    if (!e.isPrimary) return;
    const right = e.pointerType === 'mouse' && e.button === 2;
    if (!right && e.button !== 0) return;
    const from = squareAt(e.clientX, e.clientY);
    if (!from) return;
    cancel();
    if (pen || right) {
      // Pen (any finger or button) and the mouse's right button only ever draw.
      if (pen) e.stopPropagation();
      begin(e, from, true);
      return;
    }
    if (e.pointerType !== 'touch' || !holdToDraw) return;
    begin(e, from, false);
    gesture.current!.timer = window.setTimeout(() => {
      const g = gesture.current;
      if (!g) return;
      g.active = true;
      swallowTap.current = true;
      setPreview({ from: g.from, to: g.from });
      handlers.current.onHold();
    }, HOLD_MS);
  };
  return {
    cancel,
    onPointerDownCapture,
    /** A piece drag began first: this is a move, not a drawing. */
    cancelPending: () => {
      if (gesture.current && !gesture.current.active) cancel();
    },
    preview,
    /** True from the moment a press starts drawing until the next press begins. */
    quiet: () => swallowTap.current,
  };
}
